/**
 * screens/AssignmentRegister/components/LedgerDrawer
 * ──────────────────────────────────────────────────────────────────────────
 * One holder's position, the actions available on it, and its whole ledger.
 *
 * TWO DECISIONS FROM THE DESIGN THAT ARE EASY TO UNDO BY ACCIDENT:
 *
 * 1. NO PRIMARY BUTTON AMONG THE ACTIONS. The seven actions are two labelled
 *    groups of equal-weight outlined buttons. A single filled button in a row
 *    of outlined ones reads as a SELECTED FILTER, not as the recommended
 *    action — and none of these seven is more recommended than the others.
 *
 * 2. "REVERSE" APPEARS ONLY ON ROWS THAT HAVE NOT BEEN REVERSED. The ledger is
 *    append-only: a correction is a new reversing row, and a row can be
 *    reversed exactly once (a unique index enforces it). `reversed_by_id` is
 *    the fact the button reads, so a double-reversal is not merely rejected —
 *    it is not offerable.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Drawer,
  DrawerSection,
  Eyebrow,
  IconButton,
  Loading,
  Money,
} from '../../../components';
import { neutral } from '../../../theme/colors';
import { radii } from '../../../theme/radii';
import { mono, sans, size as fontSize, numeric } from '../../../theme/typography';
import { useAsyncData } from '../../../lib/useAsyncData';
import { dateTime, date } from '../../../lib/dates';
import { dotted, sentenceCase } from '../../../lib/format';
import { endRuleLabel, txnDirection, txnLabel } from '../../../lib/status';
import { fetchAssignmentDetail } from '../../../services/cash/cash.service';
import type { Transaction } from '../../../schemas/cash';
import { useShell } from '../../../context/ShellContext';
import { useConfig } from '../../../context/ConfigContext';

export interface LedgerDrawerProps {
  assignmentId: string;
  onClose: () => void;
}

export function LedgerDrawer({ assignmentId, onClose }: LedgerDrawerProps) {
  const { dataVersion, openModal } = useShell();
  const { categories } = useConfig();

  const load = useCallback(() => fetchAssignmentDetail(assignmentId), [assignmentId]);
  const { data, loading } = useAsyncData(load, [assignmentId], {
    version: dataVersion,
    errorTitle: 'Could not load the ledger',
  });

  const assignment = data?.assignment ?? null;
  const isPurpose = assignment?.scope === 'purpose';

  const categoryName = useCallback(
    (id: string | null) => categories.find((category) => category.id === id)?.code ?? null,
    [categories],
  );

  return (
    <Drawer
      visible
      onClose={onClose}
      header={
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text numberOfLines={1} style={styles.holder}>
              {assignment?.holder ?? '…'}
            </Text>
            <Text numberOfLines={1} style={styles.headerMeta}>
              {assignment
                ? dotted(
                    sentenceCase(assignment.holder_type),
                    assignment.holder_ref,
                    assignment.scope,
                    sentenceCase(assignment.instrument),
                  )
                : ''}
            </Text>
          </View>
          <IconButton icon="close" onPress={onClose} accessibilityLabel="Close the ledger" />
        </View>
      }
    >
      {loading && !assignment ? (
        <Loading />
      ) : assignment ? (
        <>
          {/* ── Position ─────────────────────────────────────────────── */}
          <DrawerSection gap={6}>
            <Text style={styles.sectionLabel}>Position</Text>
            <Money
              value={assignment.balance_minor}
              cur={assignment.cur}
              size={fontSize.drawerBalance}
              weight="500"
            />
            <Text style={styles.asOf}>
              {dotted(
                `as of ${dateTime(assignment.balance_as_of ?? assignment.opened_at)}`,
                `funding ${sentenceCase(assignment.funding)}`,
              )}
            </Text>

            {isPurpose && (
              <View style={styles.purposeBox}>
                <Text style={styles.purposeLabel}>{assignment.purpose_label ?? 'Purpose'}</Text>
                <Text style={styles.purposeMeta}>
                  {assignment.purpose_expires_at
                    ? `Expires ${date(assignment.purpose_expires_at)} — ${endRuleLabel(assignment.end_rule)}`
                    : endRuleLabel(assignment.end_rule)}
                </Text>
              </View>
            )}

            {/* ── Actions: two labelled groups, all equal weight ─────── */}
            <View style={styles.actionsBlock}>
              <Eyebrow style={styles.groupLabel}>Record a transaction</Eyebrow>
              <View style={styles.actionGrid}>
                <View style={styles.actionCell}>
                  <Button
                    label="Log spend"
                    variant="drawer"
                    size="xl"
                    fullWidth
                    onPress={() =>
                      openModal({ kind: 'transaction', assignmentId, txnType: 'spend' })
                    }
                  />
                </View>
                <View style={styles.actionCell}>
                  <Button
                    label="Collection"
                    variant="drawer"
                    size="xl"
                    fullWidth
                    onPress={() =>
                      openModal({ kind: 'transaction', assignmentId, txnType: 'collection' })
                    }
                  />
                </View>
                <View style={styles.actionCell}>
                  <Button
                    label="Deposit"
                    variant="drawer"
                    size="xl"
                    fullWidth
                    onPress={() =>
                      openModal({ kind: 'transaction', assignmentId, txnType: 'deposit' })
                    }
                  />
                </View>
                <View style={styles.actionCell}>
                  <Button
                    label="Replenish"
                    variant="drawer"
                    size="xl"
                    fullWidth
                    onPress={() =>
                      openModal({ kind: 'transaction', assignmentId, txnType: 'replenishment' })
                    }
                  />
                </View>
              </View>

              <Eyebrow style={[styles.groupLabel, styles.groupLabelSpaced]}>
                Manage the assignment
              </Eyebrow>
              <View style={styles.actionGrid}>
                <View style={styles.actionCell}>
                  <Button
                    label="Cash count"
                    variant="drawer"
                    size="xl"
                    fullWidth
                    onPress={() => openModal({ kind: 'count', assignmentId })}
                  />
                </View>
                <View style={styles.actionCell}>
                  <Button
                    label="Transfer custody"
                    variant="drawer"
                    size="xl"
                    fullWidth
                    onPress={() => openModal({ kind: 'transfer', assignmentId })}
                  />
                </View>
                {isPurpose && (
                  <View style={styles.actionCell}>
                    <Button
                      label="Close purpose"
                      variant="drawer"
                      size="xl"
                      fullWidth
                      onPress={() => openModal({ kind: 'closePurpose', assignmentId })}
                    />
                  </View>
                )}
              </View>
            </View>
          </DrawerSection>

          {/* ── The ledger ───────────────────────────────────────────── */}
          <View style={styles.ledgerHeader}>
            <Text style={styles.sectionLabel}>Transaction ledger · append-only</Text>
          </View>

          {data?.transactions.map((txn) => (
            <LedgerRow
              key={txn.id}
              txn={txn}
              categoryCode={categoryName(txn.category_id)}
              onReverse={() => openModal({ kind: 'reverse', assignmentId, txnId: txn.id })}
            />
          ))}
        </>
      ) : null}
    </Drawer>
  );
}

/* ── One ledger row ──────────────────────────────────────────────────────── */

interface LedgerRowProps {
  txn: Transaction;
  categoryCode: string | null;
  onReverse: () => void;
}

function LedgerRow({ txn, categoryCode, onReverse }: LedgerRowProps) {
  const direction = txnDirection(txn.txn_type, txn.is_reversal);
  // A row can be reversed once, and a reversal cannot itself be reversed.
  const canReverse = !txn.reversed_by_id && !txn.is_reversal;

  return (
    <View style={styles.ledgerRow}>
      <View style={styles.ledgerTop}>
        <View style={styles.ledgerText}>
          <Text style={styles.ledgerType}>{txnLabel(txn.txn_type)}</Text>
          {(categoryCode || txn.memo) && (
            <Text numberOfLines={2} style={styles.ledgerMemo}>
              {dotted(categoryCode, txn.memo)}
            </Text>
          )}
        </View>
        <Money
          value={txn.amount_minor}
          cur={txn.cur}
          direction={direction}
          align="right"
          // Inflows read at full strength; outflows sit back in grey, which is
          // how the design distinguishes them without adding a colour.
          color={direction === 'in' ? neutral.ink : neutral.grey600}
        />
      </View>

      <View style={styles.ledgerChips}>
        <Text style={styles.stamp}>{dateTime(txn.occurred_at)}</Text>
        {!!txn.document_ref && <MetaChip label={`evidence · ${txn.document_ref}`} />}
        {!!txn.owed_ref && <MetaChip label={`owed · ${txn.owed_ref}`} />}
        {!!txn.route_target && (
          <MetaChip label={txn.routed_at ? `routed · ${txn.route_target}` : `routing · ${txn.route_target}`} />
        )}
        {!!txn.route_ref && <Text style={styles.routeRef}>{txn.route_ref}</Text>}
        <View style={styles.spacer} />
        {canReverse && (
          <Button label="Reverse" size="xs" variant="outline" onPress={onReverse} />
        )}
      </View>
    </View>
  );
}

function MetaChip({ label }: { label: string }) {
  return (
    <View style={styles.metaChip}>
      <Text numberOfLines={1} style={styles.metaChipText}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  headerText: { flex: 1, minWidth: 0, gap: 1 },
  holder: { ...sans('600'), fontSize: fontSize.md, color: neutral.ink },
  headerMeta: { ...mono('400'), fontSize: fontSize.metaSm, color: neutral.grey600 },

  // 11px / 0.06em uppercase grey — the drawer's own section label, one step
  // heavier than the 10px eyebrow used for the action groups.
  sectionLabel: {
    ...sans('400'),
    fontSize: fontSize.metaSm,
    letterSpacing: 0.66,
    textTransform: 'uppercase',
    color: neutral.grey600,
  },
  asOf: { ...mono('400'), fontSize: fontSize.tableHeader, ...numeric, color: neutral.grey400, marginTop: 5 },

  purposeBox: {
    marginTop: 12,
    paddingVertical: 11,
    paddingHorizontal: 12,
    backgroundColor: neutral.surface2,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.box,
    gap: 1,
  },
  purposeLabel: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  purposeMeta: { ...sans('400'), fontSize: fontSize.label, color: neutral.grey600 },

  actionsBlock: { marginTop: 16 },
  groupLabel: { fontSize: fontSize.xxs, marginBottom: 8 },
  groupLabelSpaced: { marginTop: 14 },
  actionGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  // Two equal columns: `calc(50% - 3px)` where 6px is the gap.
  actionCell: { flexBasis: '48%', flexGrow: 1, minWidth: 130 },

  ledgerHeader: {
    paddingVertical: 11,
    paddingHorizontal: 18,
    backgroundColor: neutral.surface2,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: neutral.border,
  },
  ledgerRow: {
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderBottomWidth: 1,
    borderBottomColor: neutral.ruleLight,
  },
  ledgerTop: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  ledgerText: { flex: 1, minWidth: 0, gap: 1 },
  ledgerType: {
    ...sans('500'),
    fontSize: fontSize.body,
    color: neutral.ink,
    textTransform: 'capitalize',
  },
  ledgerMemo: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  ledgerChips: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 },
  stamp: { ...mono('400'), fontSize: fontSize.tableHeader, ...numeric, color: neutral.grey400 },
  metaChip: {
    paddingVertical: 2,
    paddingHorizontal: 7,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: 4,
    maxWidth: 240,
  },
  metaChipText: { ...mono('400'), fontSize: fontSize.eyebrow, color: neutral.grey600 },
  routeRef: { ...mono('400'), fontSize: fontSize.eyebrow, ...numeric, color: neutral.grey400 },
  spacer: { flex: 1 },
});

export default LedgerDrawer;
